import React, { useState } from 'react'
import type { WizardState } from './types'
import { SectionBlock, FieldRow, RadioPillGrid, inputStyle } from './ui'

const BLUE = '#4f46e5'

// ─── Icons ───────────────────────────────────────────────────────────────────
const IconAppliance = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="18" rx="2" />
    <path d="M8 10h8M8 14h5" />
  </svg>
)
const IconWrench = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
)
const IconHome = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
    <path d="M9 21V12h6v9" />
  </svg>
)
const IconKey = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7.5" cy="15.5" r="5.5" />
    <path d="m21 2-9.6 9.6M15.5 7.5l3 3M17 6l3 3" />
  </svg>
)

// ─── Appliance icons ──────────────────────────────────────────────────────────
const IcoWasher = (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="2"/><circle cx="12" cy="13" r="4"/><path d="M7 7h1m2 0h1"/></svg>)
const IcoDishwasher = (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="2"/><path d="M7 7h10M7 12h10M7 17h10"/></svg>)
const IcoDryer = (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="2"/><circle cx="12" cy="13" r="4"/><path d="M15 7h3"/></svg>)
const IcoFridge = (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M4 10h16M10 6v2M10 14v2"/></svg>)
const IcoFreezer = (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M4 14h16M10 17v2M10 6v5"/></svg>)
const IcoStove = (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><circle cx="7" cy="9" r="2"/><circle cx="17" cy="9" r="2"/><path d="M7 16h10"/></svg>)
const IcoHood = (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 20h12l3-8H3l3 8z"/><path d="M4 12V6h16v6"/><path d="M10 8h4"/></svg>)
const IcoCombo = (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="2"/><circle cx="12" cy="13" r="3"/><path d="M7 7h1m2 0h1m4 0h1"/></svg>)

const APPLIANCE_ICONS: Record<string, React.ReactNode> = {
  'Komfur': IcoStove,
  'Køleskab': IcoFridge,
  'Fryser': IcoFreezer,
  'Køle-/fryseskab': IcoFridge,
  'Opvaskemaskine': IcoDishwasher,
  'Vaskemaskine': IcoWasher,
  'Tørretumbler': IcoDryer,
  'Emhætte': IcoHood,
  'Vaskemaskine/tørretumbler': IcoCombo,
}
const DEFAULT_APPLIANCE_ICON = (<IconAppliance />)

// ─── Standard appliances ──────────────────────────────────────────────────────
const STANDARD_APPLIANCES = [
  'Komfur', 'Køleskab', 'Fryser', 'Køle-/fryseskab',
  'Opvaskemaskine', 'Vaskemaskine', 'Tørretumbler', 'Emhætte',
  'Vaskemaskine/tørretumbler',
]

const MAINTENANCE_OPTIONS = [
  { label: 'Udlejer', value: 'udlejer' },
  { label: 'Lejer', value: 'lejer' },
]

const PETS_OPTIONS = [
  { label: 'Ja, husdyr er tilladt', value: 'allowed' },
  { label: 'Nej, ingen husdyr er tilladt', value: 'not_allowed' },
  { label: 'Dispensation til bestemte husdyr', value: 'dispensation' },
]

// ─── ApplianceCheckbox (same style as AccessCheckbox in SectionGenerelt) ──────
function ApplianceCheckbox({ icon, label, checked, onChange }: {
  icon: React.ReactNode; label: string; checked: boolean; onChange: () => void
}) {
  return (
    <div
      onClick={onChange}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 10px', borderRadius: 7, marginBottom: 5, cursor: 'pointer',
        border: `1px solid ${checked ? BLUE : '#e8eaed'}`,
        background: checked ? '#eef2ff' : 'white',
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 7, flexShrink: 0,
        background: '#f7f8fa', display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: checked ? BLUE : '#9ca3af',
      }}>
        {icon}
      </div>
      <span style={{ flex: 1, fontSize: 13, color: '#374151', fontWeight: checked ? 500 : 400 }}>
        {label}
      </span>
      <div style={{
        width: 18, height: 18, borderRadius: 4, flexShrink: 0,
        border: `1.5px solid ${checked ? BLUE : '#d1d5db'}`,
        background: checked ? BLUE : 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && (
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.5">
            <polyline points="2 6 5 9 10 3"/>
          </svg>
        )}
      </div>
    </div>
  )
}

// ─── Toggle with optional legal tooltip ───────────────────────────────────────
function Toggle({ label, value, onChange, help, tooltip }: {
  label: string; value: boolean; onChange: (v: boolean) => void; help?: string; tooltip?: string
}) {
  const [showTip, setShowTip] = useState(false)
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, color: '#111827', fontWeight: 500 }}>{label}</span>
          {tooltip && (
            <div style={{ position: 'relative', display: 'inline-flex' }}>
              <button
                type="button"
                onMouseEnter={() => setShowTip(true)}
                onMouseLeave={() => setShowTip(false)}
                onClick={() => setShowTip(v => !v)}
                style={{
                  width: 18, height: 18, borderRadius: '50%', border: '1.5px solid #d1d5db',
                  background: '#f9fafb', color: '#6b7280', fontSize: 11, fontWeight: 700,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}
              >?</button>
              {showTip && (
                <div style={{
                  position: 'absolute', top: 24, left: 0, zIndex: 200,
                  background: 'white', border: '1px solid #e5e7eb', borderRadius: 10,
                  boxShadow: '0 4px 20px rgba(0,0,0,.12)',
                  padding: '12px 14px', width: 320, fontSize: 12, color: '#374151', lineHeight: 1.6,
                }}>
                  {tooltip}
                </div>
              )}
            </div>
          )}
        </div>
        {help && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{help}</div>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        style={{
          width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
          background: value ? BLUE : '#d1d5db', position: 'relative', flexShrink: 0,
          transition: 'background 0.15s', marginLeft: 16,
        }}
      >
        <span style={{
          position: 'absolute', top: 3, left: value ? 20 : 3,
          width: 16, height: 16, borderRadius: '50%', background: '#fff',
          transition: 'left 0.15s', display: 'block',
        }} />
      </button>
    </div>
  )
}

// ─── Legal tooltip texts ──────────────────────────────────────────────────────
const TOOLTIP_MULTIPLE_UNITS = `Spørgsmålet afgør, om du er erhvervsmæssig udlejer i lejelovens forstand. Udlejer du mere end ét lejemål til beboelse, er du forpligtet til at afholde både indflytnings- og fraflytningssyn samt overholde skærpede frister og formkrav ved opsigelse og varsling (lejelovens §98, stk. 1). Svarer du nej, er visse af disse pligter lempede, men de grundlæggende regler om lejers rettigheder gælder fortsat fuldt ud.`

const TOOLTIP_MOVE_IN = `Udlejer er efter lejelovens §9, stk. 1, forpligtet til at afholde indflytningssyn og udlevere en skriftlig indflytningsrapport til lejer senest to uger efter indflytningsdagen. Rapporten dokumenterer lejemålets stand ved overtagelsen og danner grundlag for vurderingen af normalslid. Undlader udlejer at afholde indflytningssyn eller udlevere rapporten rettidigt, mister udlejer retten til at kræve, at lejer istandsætter skader, der eksisterede ved indflytningen.`

const TOOLTIP_MOVE_OUT = `Udlejer er efter lejelovens §98, stk. 1, forpligtet til at indkalde lejer til fraflytningssyn med mindst én uges varsel og afholde synet inden for to uger efter, at udlejer er blevet bekendt med fraflytningsdatoen. Fraflytningsrapporten skal udleveres til lejer på synet eller senest to uger derefter. Overholdes fristen ikke, fortaber udlejer retten til at kræve istandsættelse ud over det, som er angivet i rapporten. Reglen gælder kun udlejere, der udlejer mere end ét lejemål til beboelse.`

// ─── Main component ───────────────────────────────────────────────────────────
export function SectionVedligehold({ state, onChange }: {
  state: WizardState
  onChange: (p: Partial<WizardState>) => void
}) {
  const [newAppliance, setNewAppliance] = useState('')

  function toggleAppliance(name: string) {
    const has = state.appliances.includes(name)
    onChange({ appliances: has ? state.appliances.filter(a => a !== name) : [...state.appliances, name] })
  }

  function addCustomAppliance() {
    const trimmed = newAppliance.trim()
    if (!trimmed || state.appliances_other.includes(trimmed)) return
    onChange({ appliances_other: [...state.appliances_other, trimmed] })
    setNewAppliance('')
  }

  function removeCustomAppliance(name: string) {
    onChange({ appliances_other: state.appliances_other.filter(a => a !== name) })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Inventar ─────────────────────────────────────────────────────── */}
      <SectionBlock icon={<IconAppliance />} title="Inventar" subtitle="Hvilket inventar tilhører udlejeren/lejemålet?">
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>Lejeren har adgang og brugsret til følgende</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
          {STANDARD_APPLIANCES.map(name => (
            <ApplianceCheckbox
              key={name}
              icon={APPLIANCE_ICONS[name] ?? DEFAULT_APPLIANCE_ICON}
              label={name}
              checked={state.appliances.includes(name)}
              onChange={() => toggleAppliance(name)}
            />
          ))}
        </div>

        {/* Custom appliances */}
        {state.appliances_other.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, marginTop: 4 }}>
            {state.appliances_other.map(name => (
              <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ flex: 1 }}>
                  <ApplianceCheckbox
                    icon={DEFAULT_APPLIANCE_ICON}
                    label={name}
                    checked
                    onChange={() => removeCustomAppliance(name)}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeCustomAppliance(name)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, flexShrink: 0, paddingBottom: 6 }}
                >×</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <input
            style={{ ...inputStyle, flex: 1 }}
            type="text"
            placeholder="Tilføj inventar (f.eks. Tørretumbler)"
            value={newAppliance}
            onChange={e => setNewAppliance(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomAppliance() } }}
          />
          <button
            type="button"
            onClick={addCustomAppliance}
            style={{ padding: '6px 14px', borderRadius: 7, border: `1px solid ${BLUE}`, background: '#eef2ff', color: BLUE, fontSize: 13, cursor: 'pointer' }}
          >+ Tilføj</button>
        </div>

        <div style={{ marginTop: 16 }}>
          <FieldRow label="Yderligere beskrivelse (valgfrit)">
            <textarea
              style={{ ...inputStyle, minHeight: 72, resize: 'vertical' } as React.CSSProperties}
              value={state.inventory_notes}
              onChange={e => onChange({ inventory_notes: e.target.value })}
              placeholder="F.eks. køleskab (god stand), komfur (ældre stand)..."
            />
          </FieldRow>
        </div>
      </SectionBlock>

      {/* ── Vedligeholdelse ──────────────────────────────────────────────── */}
      <SectionBlock icon={<IconWrench />} title="Vedligeholdelse" subtitle="Hvem har ansvaret for vedligeholdelse?">
        <FieldRow label="Indvendig vedligeholdelse påhviler" help="Dette er typisk lejeren">
          <RadioPillGrid
            options={MAINTENANCE_OPTIONS}
            value={state.interior_maintenance}
            onChange={v => onChange({ interior_maintenance: v as 'lejer' | 'udlejer' })}
          />
        </FieldRow>
        <div style={{ marginTop: 16 }}>
          <FieldRow label="Udvendig vedligeholdelse påhviler" help="Dette er typisk udlejeren">
            <RadioPillGrid
              options={MAINTENANCE_OPTIONS}
              value={state.exterior_maintenance}
              onChange={v => onChange({ exterior_maintenance: v as 'lejer' | 'udlejer' })}
            />
          </FieldRow>
        </div>
        {state.maintenance_notes ? (
          <div style={{ marginTop: 16 }}>
            <FieldRow label="Vedligeholdelsesnoter">
              <textarea
                style={{ ...inputStyle, minHeight: 72, resize: 'vertical' } as React.CSSProperties}
                value={state.maintenance_notes}
                onChange={e => onChange({ maintenance_notes: e.target.value })}
              />
            </FieldRow>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onChange({ maintenance_notes: ' ' })}
            style={{ marginTop: 12, padding: '5px 12px', borderRadius: 8, border: '1px dashed #d1d5db', background: '#fafafa', fontSize: 12, color: '#6b7280', cursor: 'pointer' }}
          >+ Tilføj note om vedligeholdelse</button>
        )}
      </SectionBlock>

      {/* ── Benyttelse ───────────────────────────────────────────────────── */}
      <SectionBlock icon={<IconHome />} title="Benyttelse" subtitle="Hvordan må lejemålet benyttes?">
        <Toggle
          label="Er rygning i lejemålet tilladt?"
          value={state.smoking_allowed}
          onChange={v => onChange({ smoking_allowed: v })}
        />
        <div style={{ paddingTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#6b7280' }}>Er husdyr tilladt?</span>
            <span style={{ fontSize: 11, color: '#dc2626' }}>*</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {PETS_OPTIONS.map(opt => (
              <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#374151', padding: '9px 12px', borderRadius: 7, border: `1px solid ${state.pets_option === opt.value ? BLUE : '#d1d5db'}`, background: state.pets_option === opt.value ? '#eef2ff' : '#fff' }}>
                <input
                  type="radio"
                  name="pets_option"
                  value={opt.value}
                  checked={state.pets_option === opt.value}
                  onChange={() => onChange({ pets_option: opt.value as WizardState['pets_option'] })}
                  style={{ accentColor: BLUE }}
                />
                {opt.label}
              </label>
            ))}
          </div>
          {state.pets_option === 'dispensation' && (
            <textarea
              style={{ ...inputStyle, minHeight: 64, resize: 'vertical', marginTop: 10 } as React.CSSProperties}
              value={state.pets_notes}
              onChange={e => onChange({ pets_notes: e.target.value })}
              placeholder="F.eks. Hund, kat af racen x, øretatovering x, sort eller navn."
            />
          )}
        </div>
      </SectionBlock>

      {/* ── Flyttesyn ────────────────────────────────────────────────────── */}
      <SectionBlock icon={<IconKey />} title="Flyttesyn" subtitle="Skal der afholdes ind- og fraflytningssyn?">
        <Toggle
          label="Udlejer du mere end ét lejemål til beboelse?"
          value={state.multiple_units}
          // Erhvervsmæssig udlejer (§98) → begge syn er pligt; slå dem til automatisk.
          onChange={v => onChange({ multiple_units: v, ...(v ? { move_in_inspection: true, move_out_inspection: true } : {}) })}
          help="Afgør, om lejeloven kræver skriftlig fraflytningsrapport"
          tooltip={TOOLTIP_MULTIPLE_UNITS}
        />
        <Toggle
          label="Afholdes der indflytningssyn?"
          value={state.move_in_inspection}
          onChange={v => onChange({ move_in_inspection: v })}
          tooltip={TOOLTIP_MOVE_IN}
        />
        {state.move_in_inspection && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '8px 0 4px' }}>
            <FieldRow label="Dato">
              <input style={inputStyle} type="date" value={state.inspection_date} onChange={e => onChange({ inspection_date: e.target.value })} />
            </FieldRow>
            <FieldRow label="Tidspunkt">
              <input
                style={inputStyle}
                type="text"
                placeholder="14:30"
                maxLength={5}
                value={state.inspection_time || ''}
                onChange={e => {
                  const raw = e.target.value
                  let v = raw.replace(/[^0-9:]/g, '')
                  const prev = state.inspection_time || ''
                  if (v.length === 2 && !v.includes(':') && raw.length >= prev.length) {
                    v = v + ':'
                  }
                  onChange({ inspection_time: v.slice(0, 5) })
                }}
                onBlur={e => {
                  const v = e.target.value
                  if (v && !/^([01]\d|2[0-3]):[0-5]\d$/.test(v)) {
                    onChange({ inspection_time: '' })
                  }
                }}
              />
            </FieldRow>
          </div>
        )}
        <Toggle
          label="Afholdes der fraflytningssyn?"
          value={state.move_out_inspection}
          onChange={v => onChange({ move_out_inspection: v })}
          tooltip={TOOLTIP_MOVE_OUT}
        />
      </SectionBlock>

    </div>
  )
}
